import { __decorate } from "tslib";
import InstructorsTableBase from '@/classes/InstructorsTableBase';
import Component from "vue-class-component";
import { Prop, Watch } from 'vue-property-decorator';
import { SERVICE_TYPE } from '@/constants/ServiceTypes';
import { InstructorTableRowType } from '@/enums/InstructorTableRowType';
import Snackbar from './Snackbar.vue';
import ConfirmDialog from './ConfirmDialog.vue';
import { NotificationTypes } from "@/enums/NotificationTypes";
import _ from 'lodash';
import moment from 'moment';
import { shortenString } from "@/plugins/helpers";
import ServiceIcon from "@/components/ServiceIcon.vue";
import { isDesktop } from '@/utils/isDesktop';
import { callFn } from '@/utils/functionsClient';
import firebase from '@/plugins/firebase';
import { collectPayment, printReceipt } from '@/utils/payments';
import { INSTRUCTOR_METHODS, IN_PERSON_METHODS, PAYMENT_METHODS, PAYMENT_METHOD, PAYMENT_STATE_COLORS, PAYMENT_STATE_TITLES, } from '@/constants/PaymentMethods';
/** What a receipt of the cell is called, by what it settles. */
const RECEIPT_TITLES = {
    sale: 'Doklad',
    topup: 'Doplatek',
    refund: 'Dobropis',
    storno: 'Vyrovnání',
};
let InstructorsTableReservedCell = class InstructorsTableReservedCell extends InstructorsTableBase {
    dataSource;
    rowData;
    cellData;
    voucherDialog = false;
    voucherCode = '';
    processingVoucher = false;
    voucherErrors = [];
    operating = false;
    dialog = false;
    paying = false;
    printing = false;
    pdfReceiptId = '';
    voiding = false;
    adjusting = false;
    settling = false;
    /** Receipts of the cell by id, for the EET state and who accepted them. */
    receipts = {};
    async useVoucher() {
        this.processingVoucher = true;
        try {
            const voucher = this.$store.getters['voucher/all']
                .find((voucher) => voucher.data.code === this.voucherCode);
            if (!voucher) {
                throw new Error('Neplatný kód');
            }
            await this.cellData.useVoucher(voucher);
            await this.updateDataSource();
            this.voucherDialog = false;
            Snackbar.success('Poukaz aplikován!');
        }
        catch (e) {
            console.error(e);
            this.voucherErrors.push(e instanceof Error ? e.message : String(e));
            this.processingVoucher = false;
        }
    }
    async clearVoucher() {
        try {
            await this.cellData.clearVoucher();
            await this.updateDataSource();
            Snackbar.success('Poukaz smazán!');
        }
        catch (e) {
            console.error(e);
            Snackbar.error('Poukaz se nepodařilo smazat!');
        }
    }
    openVoucherDialog() {
        this.processingVoucher = false;
        this.voucherErrors.splice(0, this.voucherErrors.length);
        this.voucherCode = '';
        this.voucherDialog = true;
    }
    get isDesktop() {
        return isDesktop();
    }
    get cellCaptionDesktop() {
        if (this.cellData.length === 1) {
            if (this.cellData.clients.length === 1) {
                return `${shortenString(this.cellData.clients[0].name, 14)} (${this.cellData.clients[0].age})`;
            }
            else if (this.cellData.clients.length > 1 && this.cellData.clients.length < 5) {
                return `${this.cellData.clients.length} klienti`;
            }
            else {
                return `${this.cellData.clients.length} klientů`;
            }
        }
        else {
            if (this.cellData.clients.length === 1) {
                return `${this.cellData.clients[0].name} (${this.cellData.clients[0].age})`;
            }
            else if (this.cellData.clients.length > 1 && this.cellData.clients.length < 5) {
                return `${this.cellData.clients.length} klienti`;
            }
            else {
                return `${this.cellData.clients.length} klientů`;
            }
        }
    }
    get cellCaptionMobile() {
        if (this.cellData.clients.length === 1) {
            const splitName = this.cellData.clients[0].name.split(' ');
            const firstName = splitName[0];
            return `${firstName}<br>(${this.cellData.clients[0].age})`;
        }
        else if (this.cellData.clients.length > 1 && this.cellData.clients.length < 5) {
            return `${this.cellData.clients.length} klienti`;
        }
        else {
            return `${this.cellData.clients.length} klientů`;
        }
    }
    get selected() {
        return this.$store.getters['instructorTable/isSelected'](this.dateKey, this.rowKey, this.cellData.hour);
    }
    /** Only lessons the office can actually pay may join a shared payment. */
    get selectable() {
        return this.isModerator() && this.canPay;
    }
    get dateKey() {
        return this.dataSource.date.format('YYYY-MM-DD');
    }
    /**
     * Cmd/ctrl + click collects the lesson for a payment shared with others
     * (siblings at the counter) instead of opening the detail.
     */
    cellListeners(on) {
        return {
            ...on,
            click: (event) => {
                if (!event.metaKey && !event.ctrlKey) {
                    on.click(event);
                    return;
                }
                event.preventDefault();
                if (this.selectable) {
                    this.$store.commit('instructorTable/toggleSelection', {
                        date: this.dateKey,
                        rowKey: this.rowKey,
                        hour: this.cellData.hour,
                    });
                }
            },
        };
    }
    get rowKey() {
        if (this.rowData.rowType === InstructorTableRowType.INSTRUCTOR) {
            return this.rowData.instructorId;
        }
        return String(this.rowData.blockId);
    }
    async delete() {
        if (this.operating)
            return false;
        this.operating = true;
        try {
            await callFn('commitReservedCell', {
                date: this.dataSource.date.format('YYYY-MM-DD'),
                rowKey: this.rowKey,
                op: 'delete',
                previousHour: this.cellData.hour,
            });
            return true;
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'Chyba při mazání');
            return false;
        }
        finally {
            this.operating = false;
        }
    }
    /** In-person methods for the service, the instructor's own methods on the instructor's own row. */
    get paymentMethods() {
        if (this.isModerator()) {
            return PAYMENT_METHODS.filter(method => IN_PERSON_METHODS.includes(method.id));
        }
        if (!this.isCurrentUserInRow(this.rowData)) {
            return [];
        }
        return PAYMENT_METHODS.filter(method => INSTRUCTOR_METHODS.includes(method.id));
    }
    get canPay() {
        return !this.dataSource.concluded
            && !this.cellData.isPaid
            && this.paymentMethods.length > 0;
    }
    /** The original sale, which a storno is issued against. */
    get anchorReceipt() {
        return this.cellData.receipts.find(receipt => receipt.amount > 0);
    }
    /** Conclusion has already booked the wages, so the server refuses a storno on a closed day. */
    get canVoid() {
        if (!this.anchorReceipt || this.dataSource.concluded || !this.cellData.isPaid) {
            return false;
        }
        return this.isModerator()
            || this.anchorReceipt.paidBy === this.$store.getters['user/userId'];
    }
    get paymentState() {
        return this.cellData.paymentState;
    }
    get stateColor() {
        return PAYMENT_STATE_COLORS[this.paymentState];
    }
    /** A concluded day no longer offers anything to do about an unpaid lesson. */
    get stateVisible() {
        return this.paymentState !== 'unpaid' || !this.dataSource.concluded;
    }
    get stateLabel() {
        const title = PAYMENT_STATE_TITLES[this.paymentState];
        return this.cellData.outstanding
            ? `${title} ${this.signedOutstanding},- Kč`
            : title;
    }
    get stateTooltip() {
        switch (this.paymentState) {
            case 'paid':
                return 'Tato hodina je zaplacena';
            case 'refund':
                return `Zbývá vrátit ${this.outstandingAmount},- Kč`;
            case 'topup':
                return `Zbývá doplatit ${this.outstandingAmount},- Kč`;
            default:
                return 'Tato hodina není zaplacena!';
        }
    }
    /** Magnitude of the open amount - the direction is already in the label. */
    get outstandingAmount() {
        return Math.abs(this.cellData.outstanding?.amount ?? 0);
    }
    get signedOutstanding() {
        const amount = this.cellData.outstanding?.amount ?? 0;
        return `${amount > 0 ? '+' : '−'}${Math.abs(amount)}`;
    }
    /** As `canVoid`: a moderator, or the instructor whose lesson it is. */
    get canSettle() {
        return !this.dataSource.concluded
            && (this.isModerator() || this.isCurrentUserInRow(this.rowData));
    }
    get canSettleRefund() {
        return this.canSettle && this.paymentState === 'refund';
    }
    /** Methods the open top-up may be collected by, none when nothing is owed. */
    get settleMethods() {
        return this.canSettle && this.paymentState === 'topup' ? this.paymentMethods : [];
    }
    get canAdjust() {
        if (this.dataSource.concluded) {
            return false;
        }
        if (!this.isInstructorRow()) {
            return this.isModerator();
        }
        return this.isModerator() || this.isCurrentUserInRow(this.rowData);
    }
    get hasPaymentActions() {
        return this.canAdjust
            || this.canSettleRefund
            || this.settleMethods.length > 0
            || this.canVoid;
    }
    get canManageReservation() {
        return this.isModerator() && !this.dataSource.concluded;
    }
    /** Money and reservation actions share one menu, so one button opens both groups. */
    get hasCellActions() {
        return this.hasPaymentActions || this.canManageReservation;
    }
    receiptTitle(receipt) {
        return RECEIPT_TITLES[receipt.kind] ?? 'Doklad';
    }
    issuedAt(receipt) {
        const issued = this.receiptDoc(receipt)?.issuedAt || receipt.paidAt || receipt.createdAt;
        return moment(issued).format('D.M.YYYY HH:mm');
    }
    async openPdf(receipt) {
        const pdfPath = this.receiptDoc(receipt)?.pdfPath;
        if (!pdfPath) {
            Snackbar.error('Doklad ještě nemá PDF.');
            return;
        }
        this.pdfReceiptId = receipt.receiptId;
        try {
            window.open(await firebase.storage().ref(pdfPath).getDownloadURL(), '_blank');
        }
        catch (e) {
            console.error(e);
            Snackbar.error('PDF se nepodařilo otevřít');
        }
        finally {
            this.pdfReceiptId = '';
        }
    }
    acceptedByName(receipt) {
        return this.receiptDoc(receipt)?.acceptedByName
            || this.getInstructorFullName(receipt.paidBy ?? '');
    }
    /** Settled amounts carry their own id, so the summary is found by its receipt. */
    receiptDoc(receipt) {
        return this.receipts[receipt.receiptId];
    }
    /** The receipts carry the EET state and who accepted them; the cell only knows their ids. */
    async onDialogToggle(open) {
        this.receipts = {};
        if (!open) {
            return;
        }
        try {
            // Server side, because the receipt rules hide a colleague's doklad.
            const loaded = await callFn('getReceiptSummaries', {
                receiptIds: this.cellData.receipts.map(entry => entry.receiptId),
            });
            this.receipts = loaded.reduce((all, receipt) => {
                all[receipt.receiptId] = receipt;
                return all;
            }, {});
        }
        catch (e) {
            console.error(e);
        }
    }
    async pay(method) {
        if (this.paying)
            return;
        this.paying = true;
        try {
            const result = await collectPayment('payLesson', {
                date: this.dataSource.date.format('YYYY-MM-DD'),
                rowKey: this.rowKey,
                hour: this.cellData.hour,
            }, method);
            if (result) {
                Snackbar.success(`Doklad ${result.number} vystaven.`);
                if (result.warning) {
                    Snackbar.info(result.warning);
                }
            }
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'Platbu se nepodařilo zpracovat');
        }
        finally {
            this.paying = false;
        }
    }
    async print(receipt) {
        if (this.printing)
            return;
        this.printing = true;
        try {
            await printReceipt(receipt.receiptId);
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'Doklad se nepodařilo vytisknout');
        }
        finally {
            this.printing = false;
        }
    }
    async voidPayment() {
        const anchor = this.anchorReceipt;
        if (this.voiding || !anchor)
            return;
        if (!await ConfirmDialog.open('Storno platby', `Opravdu chcete stornovat doklad ${anchor.number}?`)) {
            return;
        }
        this.voiding = true;
        try {
            const result = await callFn('voidPayment', {
                receiptId: anchor.receiptId,
            });
            Snackbar.success(`Storno doklad ${result.number} vystaven.`);
            if (result.refundRequestId) {
                Snackbar.info('Vyzvěte zákazníka k přiložení karty pro vrácení platby.');
            }
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'Platbu se nepodařilo stornovat');
        }
        finally {
            this.voiding = false;
        }
    }
    /**
     * Lengthens or shortens the lesson by an hour. On a paid lesson the server
     * leaves the price difference open as an amount to settle.
     */
    async adjust(delta) {
        if (this.adjusting)
            return;
        const length = this.cellData.length + delta;
        const dateKey = this.dataSource.date.format('YYYY-MM-DD');
        // The menu holding the button is gone by now, so the dialog carries the spinner.
        await ConfirmDialog.run(async () => {
            this.adjusting = true;
            try {
                const result = await callFn('adjustLessonLength', {
                    date: dateKey,
                    rowKey: this.rowKey,
                    hour: this.cellData.hour,
                    delta,
                });
                Snackbar.success(`Lekce má nyní ${result.length} h.`);
                if (result.obligation) {
                    Snackbar.info(result.obligation.amount > 0
                        ? `K doplacení ${result.obligation.amount},- Kč.`
                        : `K vrácení ${Math.abs(result.obligation.amount)},- Kč.`);
                }
            }
            catch (e) {
                console.error(e);
                Snackbar.error(e instanceof Error ? e.message : 'Délku lekce se nepodařilo upravit');
            }
            finally {
                this.adjusting = false;
            }
        }, delta > 0 ? 'Prodloužení lekce' : 'Zkrácení lekce', `Opravdu změnit délku lekce na ${length} h? Cena se přepočítá.`);
    }
    /**
     * Returns the overpaid amount in one step: the credit note, the counter
     * transactions and, for a card payment, the refund on the terminal. Cash
     * goes out of the drawer here and now, so it is confirmed with its amount.
     */
    async settleRefund() {
        const open = this.cellData.outstanding;
        if (this.settling || !open)
            return;
        if (open.method !== PAYMENT_METHOD.CARD && !await ConfirmDialog.open('Vrácení platby', `Vrátit klientovi ${this.outstandingAmount},- Kč v hotovosti?`)) {
            return;
        }
        this.settling = true;
        try {
            const result = await callFn('settleLessonRefund', {
                date: this.dataSource.date.format('YYYY-MM-DD'),
                rowKey: this.rowKey,
                hour: this.cellData.hour,
                entryId: open.id,
            });
            Snackbar.success(`Dobropis ${result.number} vystaven.`);
            if (result.refundRequestIds?.length) {
                Snackbar.info('Vyzvěte zákazníka k přiložení karty pro vrácení platby.');
            }
            if (result.warning) {
                Snackbar.info(result.warning);
            }
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'Platbu se nepodařilo vrátit');
        }
        finally {
            this.settling = false;
        }
    }
    /** Collects the open top-up, by the same methods as the lesson itself. */
    async settleTopUp(method) {
        const open = this.cellData.outstanding;
        if (this.settling || !open)
            return;
        this.settling = true;
        try {
            const result = await collectPayment('settleLessonTopUp', {
                date: this.dataSource.date.format('YYYY-MM-DD'),
                rowKey: this.rowKey,
                hour: this.cellData.hour,
                entryId: open.id,
            }, method);
            if (result) {
                Snackbar.success(`Doklad ${result.number} vystaven.`);
                if (result.warning) {
                    Snackbar.info(result.warning);
                }
            }
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'Doplatek se nepodařilo zpracovat');
        }
        finally {
            this.settling = false;
        }
    }
    async remove() {
        if (this.operating)
            return;
        await ConfirmDialog.run(async () => {
            if (this.cellData.voucher !== null) {
                await this.cellData.clearVoucher();
            }
            if (!(await this.delete())) {
                return;
            }
            await this.cellData.notifyInstructor(`Výuka dne ${this.dataSource.date.format('D. M. YYYY')} od ${this.cellData.hour}:00 byla zrušena`, NotificationTypes.WARNING, { name: 'InstructorsTimeScheduleWithDate', params: { date: this.dataSource.date.format('YYYY-MM-DD') } });
        }, 'Mazání záznamu', 'Opravdu si přejete záznam smazat?');
    }
    async edit() {
        const availableHourCount = this.getAvailableHourCount(this.rowData, this.cellData.hour + this.cellData.length - 1) + this.cellData.length - 1;
        //await this.delete();
        document.dispatchEvent(new CustomEvent('editClient', {
            detail: {
                from: this.getDateTimeWithHours(this.dataSource.date, this.cellData.hour),
                availableHours: availableHourCount,
                row: this.rowData,
                hour: this.cellData.hour,
                cell: this.cellData
            }
        }));
    }
    async storno() {
        if (this.operating)
            return;
        this.operating = true;
        const stornoCell = {
            ...this.cellData.toSaveObject(),
            service: SERVICE_TYPE.STORNO,
            length: 1,
        };
        try {
            await callFn('commitReservedCell', {
                date: this.dataSource.date.format('YYYY-MM-DD'),
                rowKey: this.rowKey,
                op: 'update',
                cell: stornoCell,
                previousHour: this.cellData.hour,
            });
            await this.cellData.notifyInstructor(`Výuka dne ${this.dataSource.date.format('D. M. YYYY')} od ${this.cellData.hour}:00 byla stornována`, NotificationTypes.WARNING, { name: 'InstructorsTimeScheduleWithDate', params: { date: this.dataSource.date.format('YYYY-MM-DD') } });
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'Chyba při stornu');
        }
        finally {
            this.operating = false;
        }
    }
    async cut() {
        if (this.operating)
            return;
        await ConfirmDialog.run(async () => {
            if (!(await this.delete())) {
                return;
            }
            this.copy(null, true);
            await this.cellData.notifyInstructor(`Výuka dne ${this.dataSource.date.format('D. M. YYYY')} od ${this.cellData.hour}:00 byla zrušena`, NotificationTypes.WARNING, { name: 'InstructorsTimeScheduleWithDate', params: { date: this.dataSource.date.format('YYYY-MM-DD') } });
        }, 'Přesuní záznamu', 'Opravdu si přejete záznam přesunout? Pokud vyjmutý záznam nikam nevložíte, data se ztratí.');
    }
    copy(event, cut = false) {
        const cellData = _.cloneDeep(this.cellData);
        // Payment fields belong to the server, a pasted cell is always unpaid.
        cellData.payments = [];
        cellData.payment = null;
        cellData.isPrepaid = false;
        if (!cut) {
            cellData.voucher = null;
            cellData.voucherBlockedValue = 0;
        }
        this.$store.dispatch('instructorTable/copyCell', cellData.toSaveObject());
        Snackbar.success('Zkopírováno!');
    }
    isInstructorRow() {
        return this.rowData.rowType === InstructorTableRowType.INSTRUCTOR;
    }
};
__decorate([
    Prop({ required: true })
], InstructorsTableReservedCell.prototype, "dataSource", void 0);
__decorate([
    Prop({ required: true })
], InstructorsTableReservedCell.prototype, "rowData", void 0);
__decorate([
    Prop({ required: true })
], InstructorsTableReservedCell.prototype, "cellData", void 0);
__decorate([
    Watch('dialog')
], InstructorsTableReservedCell.prototype, "onDialogToggle", null);
InstructorsTableReservedCell = __decorate([
    Component({
        components: { ServiceIcon }
    })
], InstructorsTableReservedCell);
export default InstructorsTableReservedCell;
